/* ========================================
   COMPONENTE: HEADER (NAVEGACIÓN PRINCIPAL)
   ========================================
   Propósito: Renderizar la barra de navegación superior de la aplicación
   
   Características principales:
   - Logo con enlace a inicio
   - Menú de navegación con 5 links principales
   - Dropdown desplegable para lista de talleres (Desktop y Mobile)
   - Enlaces a redes sociales (Facebook, Instagram)
   - Botón "Registrarse" para acceso a formulario de prácticas
   - Menú hamburguesa responsivo para dispositivos móviles
   - Efecto visual al hacer scroll (oscurece header)
   
   Responsividad:
   - Desktop (≥769px): Menú horizontal con hover effects
   - Tablet/Mobile (≤768px): Menú colapsable con botón hamburguesa
   
   Rutas principales:
   - / (Inicio)
   - /practicas-2026b (Formulario de registro)
   - /nosotros (Quiénes somos)
   - /talleres (Galería de talleres)
   - /conferencias (Eventos)
======================================== */

import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import '../styles/components/Header.css';

const Header = () => {
  // ========================================
  // STATE VARIABLES (VARIABLES DE ESTADO)
  // ========================================
  
  // isScrolled: Controla si el header debe aplicar estilos de "scrolled"
  // - false: Usuario está en top de página (header más transparente)
  // - true: Usuario hizo scroll (header se oscurece, mejor contraste)
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollProgressRef = useRef(null);
  
  // menuOpen: Controla si el menú hamburguesa está abierto o cerrado
  // - false: Menú colapsado (solo visible el botón hamburguesa en móvil)
  // - true: Menú expandido (muestra todos los links de navegación)
  // Se actualiza al hacer clic en el botón hamburguesa
  const [menuOpen, setMenuOpen] = useState(false);
  
  const location = useLocation();
  const talleresActive = location.pathname.startsWith('/taller') || location.pathname.startsWith('/registro-taller');
  const navLinkClass = ({ isActive }) => `nav-page-link${isActive ? ' active' : ''}`;
  
  // ========================================
  // ENLACES A REDES SOCIALES
  // ========================================
  // Array con los enlaces a las redes sociales de CEDET HEZA
  // Contiene: href (URL), label (texto alternativo), icon (icono a mostrar)
  const socialLinks = [
    {
      href: 'https://www.facebook.com/share/1FQ3QaZ3WK/',
      label: 'Facebook de CEDET HEZA',
      icon: <FaFacebook />,
    },
    {
      href: 'https://www.instagram.com/heza.cedet/',
      label: 'Instagram de CEDET HEZA',
      icon: <FaInstagram />,
    },
  ];

  // ========================================
  // EFECTO DE SCROLL DEL HEADER
  // ========================================
  // Detecta cuando el usuario hace scroll en la página y actualiza estilos.
  // 
  // Comportamiento:
  // - Si window.scrollY > 50px: Activa la clase "scrolled" (fondo más oscuro, sombra)
  // - Si window.scrollY <= 50px: Desactiva la clase "scrolled" (transparente)
  // 
  // Propósito:
  // - Al inicio (top): Header con poco contraste para no obstruir contenido
  // - Al desplazar: Header se oscurece para mejor legibilidad de navegación
  // 
  // Implementación:
  // - Agregador de EventListener en useEffect con dependencia vacía []
  // - Se ejecuta UNA SOLA VEZ al montar el componente
  // - Listener se activa en CADA evento de scroll
  // - Cleanup: removeEventListener al desmontar componente (prev. memory leaks)
  useEffect(() => {
    let frameId = null;

    const updateScrollState = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setIsScrolled(window.scrollY > 24);
      const progress = scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0;
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.transform = `scaleX(${progress})`;
      }
      frameId = null;
    };

    const handleScroll = () => {
      if (frameId === null) frameId = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className={`header-modern ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* ====================================== 
            LOGO CON MARCO VISUAL
            ======================================
            Enlaza a la página principal (/)
            Tiene marco decorativo con efecto hover
        */}
        <div className="logo-modern">
          <Link to="/">
            <img 
              src="/logo-cedet.webp" 
              alt="CEDET HEZA" 
              className="logo-image"
            />
          </Link>
        </div>
        
        {/* ======================================
            NAVEGACIÓN PRINCIPAL DEL HEADER
            ======================================
            Contiene:
            - Botón hamburguesa (menú mobile)
            - Menú de navegación con links
            - Menú desplegable de talleres
            - Enlaces a redes sociales
            - Botón "Registrarse" para prácticas
        */}
        <nav className="nav-modern">
          {/* ======================================
              BOTÓN HAMBURGUESA (MENÚ MÓVIL)
              ======================================
              Visible solo en dispositivos móviles
              Al hacer clic, abre/cierra el menú de navegación
              Tiene animación de transformación (X)
          */}
          <button 
            className={`menu-toggle-modern ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
          >
            <span className="hamburger"></span>
          </button>
          
          {/* ======================================
              MENÚ DE NAVEGACIÓN
              ======================================
              Enlaces principales del sitio:
              - Inicio
              - Prácticas 2026B
              - Nosotros
              - Talleres (con submenu desplegable)
              - Conferencias
              
              En móviles: aparece al hacer clic en hamburguesa
              En desktop: siempre visible en horizontal
          */}
          <ul id="primary-navigation" className={`nav-menu-modern ${menuOpen ? 'open' : ''}`}>
            <li><NavLink end to="/" className={navLinkClass}>Inicio</NavLink></li>
            <li><NavLink to="/practicas-2027a" className={navLinkClass}>Prácticas</NavLink></li>
            <li><NavLink to="/nosotros" className={navLinkClass}>Nosotros</NavLink></li>
            {/* ========================================
                MENÚ DESPLEGABLE DE TALLERES
                ========================================
                - On :hover (.dropdown-talleres:hover .dropdown-menu-talleres): 
                  - Muestra menú (opacity: 1, visibility: visible)
                  - Anima entrada: transform translateY y transitions suaves
                
                Respuesta:
                - Desktop: Dropdown se abre al pasar cursor (hover)
                - Mobile (max-width 768px): Dropdown expandible en menú hamburguesa
                
                Nota: Este dropdown reemplaza el simple Link a /talleres con una
                estructura que permite acceso rápido a cualquier taller desde la cabecera.
            ======================================== */}
            <li>
              <Link
                to="/taller/herramientas-practica-contable-fiscal"
                onClick={() => setMenuOpen(false)}
                className={`nav-link-talleres nav-page-link${talleresActive ? ' active' : ''}`}
                aria-current={talleresActive ? 'page' : undefined}
              >
                Talleres
              </Link>
            </li>
            <li><NavLink to="/conferencias" className={navLinkClass}>Conferencias</NavLink></li>
            {/* ========================================
                ENLACES A REDES SOCIALES (MÓVIL)
                ========================================
                Solo visible en dispositivos móviles (max-width: 768px)
                
                Contenido:
                - Etiqueta "Síguenos"
                - Lista de iconos con enlaces a:
                  * Facebook
                  * Instagram
                
                Comportamiento:
                - Abre en nueva pestaña (target="_blank")
                - Cada ícono tiene label accesible para lectores de pantalla
                
                Nota: En desktop, las redes aparecen en otra sección (header-social)
            ======================================== */}
            <li className="nav-social-mobile">
              <span>Síguenos</span>
              <div className="header-social">
                {socialLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                  >
                    {item.icon}
                  </a>
                ))}
              </div>
            </li>
          </ul>
          
          {/* ========================================
              ACCIONES DEL HEADER (DESKTOP)
              ========================================
              Contenedor que agrupa elementos que solo aparecen en desktop:
              - Iconos de redes sociales (Facebook, Instagram)
              - Botón "Registrarse" (CTA principal)
              
              Posicionamiento: Lado derecho del header
              Visibilidad: Solo en pantallas ≥ 769px
              
              El botón "Registrarse" navega a /practicas-2026b
              donde los estudiantes pueden iniciar su registro
          ======================================== */}
          <div className="header-actions">
            {/* ========================================
                REDES SOCIALES (DESKTOP)
                ========================================
                Contenedor con los iconos de redes sociales para pantallas grandes.
                
                Mapea el array socialLinks y genera un <a> por cada red:
                - href: URL de la red social
                - target="_blank": Abre en nueva pestaña
                - rel="noopener noreferrer": Seguridad (previene acceso a window.opener)
                - item.icon: Componente Font Awesome (FaFacebook, FaInstagram, etc.)
                
                Estilos CSS (.desktop-social):
                - display: none en móvil
                - display: flex en desktop
                - Iconos con efecto hover (cambio de color, escala)
            ======================================== */}
            <div className="header-social desktop-social">
              {socialLinks.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                >
                  {item.icon}
                </a>
              ))}
            </div>
            
            {/* ========================================
                BOTÓN "REGISTRARSE" (CTA PRINCIPAL)
                ========================================
                Botón de llamada a acción principal del header.
                
                Propiedades:
                - Texto: "Registrarse"
                - Ruta: /practicas-2026b
                - Clase CSS: btn btn-primary
                - Color: Verde oscuro (fondo) con texto blanco
                - Efecto hover: Transición suave, cambio de color
                
                Ubicación: Esquina superior derecha del header
                Propósito: Guiar a estudiantes al formulario de registro de prácticas
                
                El diseño visual hace que sea el elemento más destacado del header
                para maximizar conversión de clics a registros.
            ======================================== */}
            <div className="header-cta">
              <NavLink to="/practicas-2027a" className={({ isActive }) => `btn btn-primary${isActive ? ' active' : ''}`}>
                Registrarse
              </NavLink>
            </div>
          </div>
        </nav>
      </div>
      <span
        ref={scrollProgressRef}
        className="header-scroll-progress"
        aria-hidden="true"
      />
    </header>
  );
};

export default Header;
